import { useEffect, useMemo, useId, useState, type JSX } from 'react';
import { toast } from '@/lib/toast';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { Button, DialogClose } from '../../../../components';
import type { RefrigerationUnit } from '@/data/schemas';
import type {
  RefrigerationUnitInput,
  RefrigerationUnitUpdateInput,
} from '@/data/repos/refrigerationUnitsRepo';
import {
  buildCoreDefaultValues,
  coreFormValuesToInput,
  RefrigerationUnitCoreFormError,
  type RefrigerationUnitCoreFormValues,
} from './config/core';
import { RefrigerationUnitCoreForm } from './RefrigerationUnitCoreForm';
import {
  EntityDialog,
  EntityDialogFooter,
  FormErrorBanner,
  FormErrorSummary,
} from '@/components/blocks/forms';
import { getErrorCount, scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { ConfirmDialog } from '@/components/blocks/feedback/ConfirmDialog';

interface RefrigerationUnitModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  refrigerationUnit?: RefrigerationUnit | null;
  onOpenChange: (open: boolean) => void;
  contextLine?: string;
  /** Returns the created unit ID */
  onCreate?: (input: RefrigerationUnitInput) => Promise<string> | string;
  /** Called AFTER modal closes - safe for navigation */
  onCreated?: (unitId: string) => void;
  onUpdate?: (input: RefrigerationUnitUpdateInput) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  isSubmitting?: boolean;
  isDeleting?: boolean;
  onUpdated?: (unit: RefrigerationUnit) => void;
}

export function RefrigerationUnitModal({
  open,
  mode,
  refrigerationUnit,
  onOpenChange,
  contextLine,
  onCreate,
  onCreated,
  onUpdate,
  onDelete,
  isSubmitting = false,
  isDeleting = false,
  onUpdated,
}: RefrigerationUnitModalProps): JSX.Element {
  const formId = useId();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const defaultValues = useMemo(
    () => buildCoreDefaultValues(mode === 'edit' ? refrigerationUnit : null),
    [refrigerationUnit, mode]
  );

  const form = useForm<RefrigerationUnitCoreFormValues>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, defaultValues, form]);

  const handleSubmit: SubmitHandler<RefrigerationUnitCoreFormValues> = async (values) => {
    try {
      const input = coreFormValuesToInput(values, {
        existingId: refrigerationUnit?.refrigerationUnitId,
      });
      if (mode === 'create') {
        if (!onCreate) {
          throw new Error('Funzione di creazione non disponibile');
        }
        const unitId = await onCreate(input);
        toast.success('Impianto aggiunto con successo');
        form.reset(buildCoreDefaultValues(null));
        onOpenChange(false);
        // Call onCreated AFTER modal closes - safe for navigation
        onCreated?.(unitId);
        return;
      } else if (refrigerationUnit) {
        if (!onUpdate) {
          throw new Error('Funzione di aggiornamento non disponibile');
        }
        await onUpdate({ id: refrigerationUnit.id, ...input });
        if (onUpdated) {
          const nextTechnical = input.technical
            ? {
                ...refrigerationUnit.technical,
                ...input.technical,
              }
            : refrigerationUnit.technical;
          const normalizedUnit = {
            ...refrigerationUnit,
            ...input,
            technical: nextTechnical,
          } as RefrigerationUnit;
          onUpdated(normalizedUnit);
        }
        toast.success('Impianto aggiornato');
      }
      form.reset(buildCoreDefaultValues(null));
      onOpenChange(false);
    } catch (error) {
      if (error instanceof RefrigerationUnitCoreFormError) {
        if (error.field) {
          form.setError(error.field, {
            type: 'manual',
            message: error.message,
          });
        } else {
          form.setError('root', {
            type: 'manual',
            message: error.message,
          });
        }
        return;
      }
      if (
        error &&
        typeof error === 'object' &&
        typeof (error as { field?: unknown }).field === 'string'
      ) {
        const field = (error as { field: string }).field;
        const message = error instanceof Error ? error.message : 'Campo non valido.';
        form.setError(field as keyof RefrigerationUnitCoreFormValues, {
          type: 'manual',
          message,
        });
        return;
      }
      form.setError('root', {
        type: 'manual',
        message: 'Impossibile salvare. Riprova o contatta l’assistenza.',
      });
    }
  };

  const handleDelete = async () => {
    if (!refrigerationUnit || !onDelete) return;
    try {
      await onDelete();
      toast.success('Impianto archiviato');
      onOpenChange(false);
    } catch {
      form.setError('root', {
        type: 'manual',
        message: 'Impossibile archiviare. Riprova o contatta l’assistenza.',
      });
    }
  };

  const isDirty = form.formState.isDirty;
  const errorCount = getErrorCount(form.formState.errors);
  const rootError =
    (form.formState.errors as { root?: { message?: string } }).root?.message ?? null;

  return (
    <>
      <EntityDialog
        open={open}
        onOpenChange={onOpenChange}
        title={mode === 'create' ? 'Nuovo impianto' : 'Modifica impianto'}
        size="md"
        isDirty={isDirty}
        isSaving={isSubmitting}
        formId={formId}
        contextLine={contextLine}
        banner={rootError ? <FormErrorBanner message={rootError} /> : null}
        summary={
          errorCount > 0 ? (
            <FormErrorSummary
              count={errorCount}
              onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
            />
          ) : null
        }
        footer={
          <EntityDialogFooter
            extra={
              mode === 'edit' && onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setConfirmOpen(true)}
                  disabled={isDeleting || isSubmitting}
                >
                  {isDeleting ? 'Archiviazione…' : 'Archivia impianto'}
                </Button>
              ) : null
            }
            secondary={
              <DialogClose asChild>
                <Button variant="ghost" disabled={isSubmitting || isDeleting}>
                  Chiudi
                </Button>
              </DialogClose>
            }
            primary={
              <Button type="submit" form={formId} disabled={!isDirty || isSubmitting || isDeleting}>
                {isSubmitting ? 'Salvataggio…' : mode === 'create' ? 'Crea impianto' : 'Salva'}
              </Button>
            }
          />
        }
      >
        <FormProvider {...form}>
          <form
            id={formId}
            onSubmit={form.handleSubmit(handleSubmit, (errors) => scrollToFirstError(errors))}
            noValidate
            className="space-y-4"
          >
            <fieldset disabled={isSubmitting || isDeleting}>
              <RefrigerationUnitCoreForm />
            </fieldset>
          </form>
        </FormProvider>
      </EntityDialog>

      {onDelete ? (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Archiviare impianto?"
          message="L’elemento verrà archiviato e non sarà più visibile nelle liste."
          cancelLabel="Annulla"
          confirmLabel="Archivia"
          variant="danger"
          onConfirm={handleDelete}
          isLoading={isDeleting}
        />
      ) : null}
    </>
  );
}
